import type { Logger } from "@expert/logging";

import { AnalyticsProvider, useReactAnalytics } from "@asurion-core/eventualize-react";
import { type GuideCommand, Timeline } from "@asurion-core/expert-workspace-timeline";
import { type ContextProps, useFeatures, type WorkspaceFeatures } from "@expert/features";
import { SaleProgress, useProductOfferStore } from "@expert/sales-tools";
import { Stack } from "@mantine/core";
import { useMemo } from "react";

import { DisclosureModalController } from "@/components/chat/ChatSmartOffer/DisclosureModalController";
import { useSmartOfferActions } from "@/components/chat/ChatSmartOffer/useSmartOfferActions";
import { SimplrWebsocketBadge } from "@/components/gaia/SimplrWebsocketBadge";
import { env } from "@/config";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { usePilots } from "@/pilot";
import { isChatTask, useActiveTask, useAgentFriendlyName, usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { userCache } from "@/shared-utils";

import { IssueSummary } from "./IssueSummary";
import classes from "./PlatformTimeline.module.css";
import { useSetupSubscriptions } from "./state";
import { features } from "./utils";

function SetupSubscriptions({ loggerWithContext }: { loggerWithContext: Logger }) {
    useSetupSubscriptions(loggerWithContext);

    return <></>;
}

export function PlatformTimeline({ guide }: { guide?: GuideCommand }) {
    const logger = useLogger({
        rootModule: "PlatformTimeline",
        supportTeam: "solve",
    });
    const task = useActiveTask();
    const isChat = isChatTask(task);
    // TODO(afd): Revisit this to properly handle nonvoice tasks and not unsafely cast this
    const callSid = (task as VoiceTask)?.agentCallId;
    const chatId = isChat ? task.chatId : undefined;
    const expertId = userCache.employeeId ?? "";
    const expertName = useAgentFriendlyName();
    const sessionId = useSessionId();
    const partner = usePartner();
    const chatTask = isChat ? task : undefined;
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const smartOfferActions = useSmartOfferActions(selectedProduct, chatTask);
    const isSaleSuccessOrDeclined =
        selectedProduct?.saleProgress === SaleProgress.Success ||
        selectedProduct?.saleProgress === SaleProgress.Declined;

    const {
        features: { isVerizonCareEnabled, isChatIssueSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { ozmo: isOzmoEnabled } = usePartnerFeatureConfigured();
    const { gaiaPayloadProps } = usePartnerConfig();
    const agentSite = useAgentAttributesStore((state) => state.site);

    const { pilots } = usePilots();

    const lob = pilots.includes("care") && isVerizonCareEnabled ? "care" : undefined;

    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = dispatcher
        .withIdentities({
            ...(expertId && { ExpertId: expertId }),
            ...(callSid && { CallSid: callSid }),
            ...(chatId && { ChatId: chatId }),
        })
        .withExtra({ tool: "expert-assist" });

    const featuresContext = useMemo(() => {
        return {
            appName: "expert-workspace",
            employeeId: expertId,
            environment: env.mode,
            channel: env.channel,
            partner,
            source: "expert-ui",
            lob,
            agentSite,
        } as ContextProps;
    }, [expertId, lob, partner, agentSite]);

    const IssueSummaryComponent = useMemo(() => {
        return (
            <>
                {isChat && isChatIssueSummaryEnabled && (
                    <IssueSummary activeTask={task} logger={logger} gaiaPayloadProps={gaiaPayloadProps} />
                )}
            </>
        );
    }, [isChat, isChatIssueSummaryEnabled, logger, task, gaiaPayloadProps]);

    return (
        <AnalyticsProvider dispatcher={enhancedDispatcher}>
            <Stack gap="0" className={classes.timeline}>
                <SimplrWebsocketBadge />
                <Timeline
                    gaiaPayloadProps={gaiaPayloadProps}
                    callSid={callSid}
                    chatId={chatId}
                    defaultFeatures={features}
                    expertId={expertId}
                    expertName={expertName}
                    featuresContext={featuresContext}
                    toolboxContext={{ guide, isOzmoEnabled }}
                    logger={logger}
                    lob={lob}
                    partner={partner}
                    sessionId={sessionId}
                    smartOfferActions={smartOfferActions}
                    SubscriptionsWithFeatures={isChat ? <></> : <SetupSubscriptions loggerWithContext={logger} />}
                    IssueSummaryComponent={IssueSummaryComponent}
                    isSaleSuccessOrDeclined={isSaleSuccessOrDeclined}
                />
            </Stack>
            <DisclosureModalController />
        </AnalyticsProvider>
    );
}
